import { ExoticComponent, HTMLAttributes, ReactNode } from 'react';
import classNames from 'classnames';

type ContainerDirection = 'horizontal' | 'vertical';

interface ContainerProps {
    /**
     * Render the Container as a different element.
     */
    as?: string | ExoticComponent<any>;

    /**
     * Controls the visibility of the container. Defaults to: "true".
     */
    show?: boolean;

    /**
     * Render container as inline component.
     */
    inline?: boolean;

    /**
     * Specifies if a container should grow
     */
    grow?: boolean;

    /**
     *
     * @param visible A boolean which is the result of the "show" param input.
     * @returns A
     */
    render?: (visible: boolean) => ReactNode;

    /**
     * The direction that all direct childs need to be facing,
     * either "horizontal" or "vertical". Defaults to: "horizontal"
     * when ommitted.
     */
    direction?: ContainerDirection;

    /**
     * These class names that will be merged with the container's own
     * class names.
     */
    className?: string;

    /**
     * The default React children prop.
     */
    children?: ReactNode;
}

/**
 * This component is a wrapper which supports providing
 * a "show" prop which shows/hides the div.
 *
 * @author Dwayne Boumann <boumann@creativeshift.nl>
 * @param {ContainerProps} props
 */
const Container = ({
    as = 'div',
    show = true,
    direction = 'horizontal',
    className = '',
    grow = false,
    inline,
    render,
    children,
    ...inherited
}: ContainerProps) => {
    const Component: string | ExoticComponent<any> = as;

    const containerStyle: HTMLAttributes<any> = {};

    /**
     * Fragments can't have styles applied upon them, so we need
     * to rule them out.
     */
    if (! Component.toString().includes('react.fragment')) {
        containerStyle.className = classNames({
            [className]: className.length > 0,
            'relative': ! ['static', 'fixed', 'absolute', 'sticky'].some(position => className.includes(position)),

            'flex': ! inline,
            'inline-flex': inline,
            'flex-row': direction === 'horizontal',
            'flex-col': direction === 'vertical',
            'flex-grow': grow
        });
    }

    if (typeof render === 'function') {
        return show == null || (typeof show === 'boolean' && show === true)
            ? <Component {...containerStyle} {...inherited}>{render(Boolean(show))}</Component>
            : null;
    }
    else if (show && children) {
        return (
            <Component {...containerStyle} {...inherited}>{children}</Component>
        );
    }

    return null;
};

export { Container };